import {
  Component, ElementRef, ChangeDetectionStrategy, Input, Output, EventEmitter, ViewChild,
  Inject, ChangeDetectorRef, OnDestroy
} from '@angular/core';

import { RANDOM_ID } from '@app/constants/general.constants';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { SearchType } from '@search-bar/entities/search-type';
import { TOGGLE_SEARCH_PAGE_DELAY } from '@shared/base-search-bar/constants/search.constants';
import { TooltipOrientation } from '@app/modules/shared/tooltip/entities/tooltip-orientation';
import { EventProperties } from '@avo/interface/trigger-props';
import { locationNotFoundAmplitudePropsMapper } from '@app/modules/core/mappers/amplitude-mappers';
import { BaseSearchInputAbstractComponent } from '@shared/base-search-bar/abstract/base-search-input.abstract';
import { InputTextSize } from '@design-system/components/molecules/input-text/entities/input-text.types';
import {
  AutoCompleteExtraActivity, AutoCompleteExtraDestination, AutoCompleteTravelExtra, InputTravelExtra
} from '@shared/base-search-bar/entities/autocomplete-travel-extra';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { ActivitySearchCodeType } from '@activities/entities/activities-acutocomplete';

@Component({
  selector: 'app-search-input-activity',
  templateUrl: './search-input-activity.component.html',
  styleUrls: ['./search-input-activity.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SearchInputActivityComponent extends BaseSearchInputAbstractComponent implements OnDestroy {

  @Input() searchType: SearchType;
  @Input() input: InputTravelExtra;
  @Input() countryCode: string;
  @Input() countryName: string;
  @Input() set autoCompletePlaces(autoCompletePlaces: AutoCompleteTravelExtra) {
    this._autoCompletePlaces = autoCompletePlaces;
    setTimeout(() => this.sendNotFoundEvent(), TOGGLE_SEARCH_PAGE_DELAY);
  }
  @Input() delay: number = 500;
  @Input() eventPath: EventTarget[];
  @Input() isMobile: boolean = false;
  @Input() autofocus: boolean = false;
  @Input() selected: boolean;
  @Input() isLoadingAutocompletePlaces: boolean = false;
  @Input() resultsPage = false;

  @Output() selectInput: EventEmitter<InputTravelExtra> = new EventEmitter<InputTravelExtra>();
  @Output() locationNotFoundEvent: EventEmitter<EventProperties> = new EventEmitter<EventProperties>();
  @Output() popularSelected: EventEmitter<EventProperties> = new EventEmitter<EventProperties>();

  @ViewChild('destination', { static: true }) public destination: ElementRef;

  public id: number = Math.floor(Math.random() * RANDOM_ID);
  public tooltipOrientation: string = TooltipOrientation.LEFT;
  private _autoCompletePlaces: AutoCompleteTravelExtra;

  constructor(
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    super(changeDetectorRef);
  }

  ngOnDestroy(): void {
    this.removeAutoCompletePlaces.emit();
  }

  get inputSize(): InputTextSize {
    return !this.isMobile && (this.isResultsPage) ? InputTextSize.MD : InputTextSize.RG;
  }

  get filled(): boolean {
    return !this.isMobile && !this.isResultsPage;
  }

  get staticFilled(): boolean {
    return !this.isMobile && (this.isResultsPage);
  }

  get sendLocationNotFoundEvent(): boolean {
    return this.showInput && !!this.value && this.resultsPlaces?.activities?.length === 0 &&
      this.resultsPlaces?.destinations?.length === 0;
  }

  get autoCompletePlaces(): AutoCompleteTravelExtra {
    return this._autoCompletePlaces;
  }

  get resultsPlaces(): AutoCompleteTravelExtra {
    return { activities: this.autoCompletePlaces?.activities, destinations: this.autoCompletePlaces?.destinations };
  }

  get isResultsPage(): boolean {
    return !this.isMobile ? this.resultsPage : false;
  }

  get inputValue(): string {
    const location = this.input?.cityName && this.input?.countryName ?
      `${this.input?.cityName}, ${this.input?.countryName}` : this.input?.cityName && !this.input?.countryName ?
        `${this.input?.cityName}` : !this.input?.cityName && this.input?.countryName ? `${this.input?.countryName}` : '';
    return this.input?.codeType === ActivitySearchCodeType.Activity && location ?
      `${this.input?.activityName}, ${location}` : this.input?.codeType === ActivitySearchCodeType.Activity ?
        this.input?.activityName : location;
  }

  public selectActivityEvent(data: AutoCompleteExtraActivity): void {
    this.toggleInput();
    this.selectInput.emit({
      Id: data.id,
      codeType: ActivitySearchCodeType.Activity,
      activityName: data.activityName,
      activityType: data.activityType,
      cityName: data.cityName,
      countryName: data.countryName,
    });
  }

  public selectExtraDestinationEvent(data: AutoCompleteExtraDestination): void {
    this.toggleInput();
    this.selectInput.emit({
      Id: data.cityCode,
      codeType: ActivitySearchCodeType.CityCode,
      cityName: data.cityName,
      countryName: data.countryName,
    });
  }

  public selectPopularPlace(place: AutocompletePlace): void {
    this.toggleInput();
    this.selectInput.emit({
      Id: place.code,
      codeType: ActivitySearchCodeType.CityCode,
      cityName: place.description,
      countryName: place.country,
      nearestIata: place.code,
      isPopularSelection: true,
    });
  }


  private sendNotFoundEvent(): void {
    if (this.sendLocationNotFoundEvent) {
      this.locationNotFoundEvent.emit(
        locationNotFoundAmplitudePropsMapper(this.searchType, this.value, this.input?.Id));
    }
  }
}
